import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../auth/SupabaseClient";
import {
  ArrowLeft,
  Save,
  User,
  Mail,
  Phone,
  Calendar,
  Truck,
  CreditCard,
  DollarSign,
  MapPin,
  PlusCircle,
  Trash2,
  Search,
} from "lucide-react";
import DeliveryAddress from "../../components/DeliveryAddress";

interface Product {
  id: string;
  name: string;
  price: number;
}

interface OrderProduct {
  id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  product: Product;
}

interface Order {
  client_id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  status: string;
  delivery_method: string;
  payment_status: string;
  payment_method: string;
  total_amount: number;
  delivery_address: string;
  notes: string;
  products: OrderProduct[];
}

interface Client {
  id: string;
  name: string;
  email: string;
  telephone: string;
}

const CreateOrder: React.FC = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order>({
    client_id: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    status: "En attente",
    delivery_method: "Livraison",
    payment_status: "Non payé",
    payment_method: "Carte de crédit",
    total_amount: 0,
    delivery_address: "",
    notes: "",
    products: [],
  });
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showClientSearch, setShowClientSearch] = useState<boolean>(false);
  const [productForm, setProductForm] = useState({
    product_id: "",
    quantity: 1,
  });
  const [productFormError, setProductFormError] = useState<string | null>(null);

  const inputClass =
    "bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-gray-900 dark:text-white";

  useEffect(() => {
    fetchAvailableProducts();
    fetchClients();
  }, []);

  const fetchAvailableProducts = async () => {
    try {
      const { data, error } = await supabase.from("products").select("*");
      if (error) throw error;
      setAvailableProducts((data as Product[]) || []);
    } catch (error) {
      console.error("Error fetching available products:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .order("name");
      if (error) throw error;
      setClients((data as Client[]) || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setOrder((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClientSelect = (client: Client) => {
    setOrder((prev) => ({
      ...prev,
      client_id: client.id,
      client_name: client.name,
      client_email: client.email,
      client_phone: client.telephone,
    }));
    setShowClientSearch(false);
    setSearchTerm("");
  };

  const handleProductFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setProductForm((prev) => ({ ...prev, [name]: value }));
    setProductFormError(null);
  };

  const handleAddProduct = (e: React.FormEvent) => {
    e.preventDefault();
    if (!productForm.product_id) {
      setProductFormError("Please select a product");
      return;
    }
    const productToAdd = availableProducts.find(
      (p) => p.id === productForm.product_id
    );
    if (!productToAdd) {
      setProductFormError("Selected product not found");
      return;
    }

    const quantity = parseInt(productForm.quantity.toString());
    if (isNaN(quantity) || quantity < 1) {
      setProductFormError("Please enter a valid quantity");
      return;
    }

    const newProduct: OrderProduct = {
      id: Date.now().toString(),
      product_id: productToAdd.id,
      quantity: quantity,
      unit_price: productToAdd.price,
      total_price: quantity * productToAdd.price,
      product: productToAdd,
    };

    setOrder((prev) => ({
      ...prev,
      products: [...prev.products, newProduct],
      total_amount: prev.total_amount + newProduct.total_price,
    }));

    setProductForm({ product_id: "", quantity: 1 });
    setProductFormError(null);
  };

  const handleRemoveProduct = (productId: string) => {
    setOrder((prev) => {
      const updatedProducts = prev.products.filter((p) => p.id !== productId);
      const newTotalAmount = updatedProducts.reduce(
        (total, product) => total + product.total_price,
        0
      );
      return {
        ...prev,
        products: updatedProducts,
        total_amount: newTotalAmount,
      };
    });
  };

  const handleSave = async () => {
    try {
      const { data: orderData, error: orderError } = await supabase
        .from("orders")
        .insert({
          client_id: order.client_id,
          status: order.status,
          delivery_method: order.delivery_method,
          payment_status: order.payment_status,
          payment_method: order.payment_method,
          total_amount: order.total_amount,
          delivery_address: order.delivery_address,
          notes: order.notes,
        })
        .select("id")
        .single();

      if (orderError) throw orderError;
      if (!orderData) throw new Error("No order data returned");

      const orderProducts = order.products.map((product) => ({
        order_id: orderData.id,
        product_id: product.product_id,
        quantity: product.quantity,
        unit_price: product.unit_price,
      }));

      const { error: productsError } = await supabase
        .from("order_products")
        .insert(orderProducts);

      if (productsError) throw productsError;

      navigate(`/dashboard/orders/${orderData.id}`);
    } catch (error) {
      console.error("Error creating order:", error);
      // Handle error (e.g., show error message to user)
    }
  };

  const filteredClients = clients.filter((client) =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderClientInfo = () => (
    <div className="col-span-1 md:col-span-2 bg-white dark:bg-gray-800 rounded-lg ">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Client Information
        </h2>
        <button
          onClick={() => setShowClientSearch(!showClientSearch)}
          className={`px-4 py-2 rounded-md text-sm font-medium transition duration-150 ease-in-out ${
            showClientSearch
              ? "bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              : "bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
          }`}
        >
          {showClientSearch ? "Cancel" : "Select Client"}
        </button>
      </div>

      {showClientSearch ? (
        <div className="space-y-4">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={handleClientSearch}
              placeholder="Search clients..."
              className="w-full px-4 py-2 pl-10 pr-4 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <ul className="mt-2 max-h-60 overflow-auto rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600">
            {filteredClients.map((client) => (
              <li
                key={client.id}
                onClick={() => handleClientSelect(client)}
                className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition duration-150 ease-in-out"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {client.name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {client.email}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <User className="w-6 h-6 mr-3 text-blue-500 dark:text-blue-400" />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {order.client_name || "No client selected"}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Name
              </div>
            </div>
          </div>
          <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <Mail className="w-6 h-6 mr-3 text-blue-500 dark:text-blue-400" />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {order.client_email || "N/A"}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Email
              </div>
            </div>
          </div>
          <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <Phone className="w-6 h-6 mr-3 text-blue-500 dark:text-blue-400" />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {order.client_phone || "N/A"}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Phone
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderOrderDetails = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg ">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Order Details
      </h2>
      <div className="space-y-4">
        <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <Calendar className="w-6 h-6 mr-3 text-blue-500 dark:text-blue-400" />
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {new Date().toLocaleDateString()}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Order Date
            </div>
          </div>
        </div>

        <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <Truck className="w-6 h-6 mr-3 text-blue-500 dark:text-blue-400 flex-shrink-0" />
          <div className="flex-grow">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Delivery Method
            </div>
            <select
              name="delivery_method"
              value={order.delivery_method}
              onChange={handleInputChange}
              className="w-full bg-white dark:bg-gray-600 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-500 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Livraison">Livraison</option>
              <option value="Envoi">Envoi</option>
              <option value="Retrait">Retrait</option>
            </select>
          </div>
        </div>

        <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <CreditCard className="w-6 h-6 mr-3 text-blue-500 dark:text-blue-400 flex-shrink-0" />
          <div className="flex-grow">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Payment Method
            </div>
            <select
              name="payment_method"
              value={order.payment_method}
              onChange={handleInputChange}
              className="w-full bg-white dark:bg-gray-600 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-500 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Carte de crédit">Carte de crédit</option>
              <option value="PayPal">PayPal</option>
              <option value="Virement bancaire">Virement bancaire</option>
              <option value="Espèces">Espèces</option>
              <option value="Revolut">Revolut</option>
            </select>
          </div>
        </div>

        <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <DollarSign className="w-6 h-6 mr-3 text-blue-500 dark:text-blue-400 flex-shrink-0" />
          <div className="flex-grow">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Payment Status
            </div>
            <select
              name="payment_status"
              value={order.payment_status}
              onChange={handleInputChange}
              className="w-full bg-white dark:bg-gray-600 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-500 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Non payé">Non payé</option>
              <option value="Payé">Payé</option>
              <option value="Remboursé">Remboursé</option>
              <option value="En attente de paiement">
                En attente de paiement
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDeliveryAddress = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
      <div className="relative">
        <DeliveryAddress
          handleInputChange={handleInputChange}
        />
      </div>
    </div>
  );

  const renderOrderItems = () => (
    <div className="p-6 md:p-8 border-t border-gray-200 dark:border-gray-700">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Order Items
      </h2>
      <form onSubmit={handleAddProduct} className="mb-4">
        <div className="flex items-end space-x-4">
          <div className="flex-grow">
            <label
              htmlFor="product_id"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Product
            </label>
            <select
              id="product_id"
              name="product_id"
              value={productForm.product_id}
              onChange={handleProductFormChange}
              className={inputClass + " w-full"}
            >
              <option value="">Select a product</option>
              {availableProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} - {product.price.toFixed(2)} €
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="quantity"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Quantity
            </label>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={productForm.quantity}
              onChange={handleProductFormChange}
              min="1"
              className={inputClass + " w-20"}
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600 transition duration-150 ease-in-out"
          >
            <PlusCircle className="w-5 h-5 inline mr-2" />
            Add
          </button>
        </div>
      </form>
      {productFormError && (
        <p className="text-red-500 text-sm mt-2">{productFormError}</p>
      )}
      <div className="overflow-x-auto mt-4">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Product
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Quantity
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Unit Price
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Total
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700">
            {order.products.map((product) => (
              <tr
                key={product.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  {product.product.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {product.quantity}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {product.unit_price.toFixed(2)} €
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {product.total_price.toFixed(2)} €
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleRemoveProduct(product.id)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderOrderTotal = () => (
    <div className="p-6 md:p-8 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
      <div className="flex justify-end">
        <div className="w-full max-w-md">
          <div className="flex justify-between py-2 text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">Subtotal</span>
            <span>{order.total_amount.toFixed(2)} €</span>
          </div>
          <div className="flex justify-between py-2 text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">Shipping</span>
            <span>Included</span>
          </div>
          <div className="flex justify-between py-4 text-lg font-bold text-gray-900 dark:text-white border-t border-gray-200 dark:border-gray-700 mt-2">
            <span>Total</span>
            <span>{order.total_amount.toFixed(2)} €</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotes = () => (
    <div className="p-6 md:p-8 border-t border-gray-200 dark:border-gray-700">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Notes
      </h2>
      <textarea
        name="notes"
        value={order.notes}
        onChange={handleInputChange}
        className={inputClass + " w-full p-4"}
        rows={4}
        placeholder="Add any additional notes here..."
      />
    </div>
  );

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="mx-auto bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
            <button
              onClick={() => navigate("/dashboard/orders")}
              className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition duration-150 ease-in-out mb-4 md:mb-0"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to orders
            </button>
          </div>
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Create New Order
            </h1>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 transition duration-150 ease-in-out"
            >
              <Save className="w-4 h-4 inline mr-2" />
              Save Order
            </button>
          </div>
        </div>

        {/* Client and Order Info */}
        <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {renderClientInfo()}
          {renderOrderDetails()}
        </div>

        {/* Delivery Address */}
        {renderDeliveryAddress()}

        {/* Order Items */}
        {renderOrderItems()}

        {/* Order Total */}
        {renderOrderTotal()}

        {/* Notes */}
        {renderNotes()}
      </div>
    </div>
  );
};

export default CreateOrder;
